import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { MarkerIcon } from "@images/svgIcons/markerIcon"
import { type Address } from "@modules/address/addressTypes"
import { word } from "@modules/core/utils/i18n"
import { useMemo } from "react"
import { Link } from "react-router"
import styled from "styled-components"

type LocationBoxProps = {
  box: {
    address: Address
    name: string
  }
  bgColor?: {
    container?: string
    icon?: string
  }
} & React.ComponentProps<"div">

export function LocationBox({ box, bgColor, className, ...props }: LocationBoxProps) {
  const { theme } = useTheme()

  const mapsAddress = "http://maps.google.com/?q="

  const boxAddress = useMemo(() => {
    let address: string
    if (box.address.additionalInformation) {
      address = `${box.address.address},${box.address.additionalInformation}, ${box.address.postalCode} ${box.address.city}`
    } else {
      address = `${box.address.address}, ${box.address.postalCode} ${box.address.city}`
    }
    return address
  }, [box.address])

  return (
    <div className={cn("rounded-3xl bg-surface-secondary p-5", className)} {...props}>
      <div className="mb-2 flex items-center">
        <div>
          <Round $bgColor={bgColor?.icon}>
            <MarkerIcon color="currentColor" className="text-textPrimary-on-color" />
          </Round>
        </div>
        <div className="flex flex-col">
          <Text
            fontStyle="TS"
            color="currentColor"
            className={cn("text-textOnColor-heading", { "text-white": theme === Theme.OwnerDark })}
          >
            {box.name}
          </Text>
          <Text
            fontStyle="BM"
            color="currentColor"
            className={cn("text-textOnColor-caption", { "text-neutral-600": theme === Theme.OwnerDark })}
          >
            {boxAddress}
          </Text>
        </div>
      </div>

      <Button
        variant="secondary"
        size="lg"
        className="w-full"
        nativeButton={false}
        render={
          <Link to={`${mapsAddress}${box.name} ${boxAddress}`} target="_blank">
            {word("box.where.button")}
          </Link>
        }
      />
    </div>
  )
}

const Round = styled.div<{ $bgColor?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 66px;
  height: 66px;
  border-radius: 50%;
  background-color: ${({ $bgColor }) => $bgColor ?? "var(--secondary-600)"};
  margin-right: 10px;
`
